import * as dm from '../../data-models';

/**
 * Filters personData?generated?parentNTNs - claims/leaves/accommodations to the last 1 year.
 * - Claims: keep if createdDate >= (today - 1 year)
 * - Leaves: keep if startDate >= (today - 1 year)
 * - Accommodations: keep if createdDate >= (today - 1 year)
 * - filters personData.claimsInProcess - keep if startDate >= (today - 1 year)
 *
 * Dates are interpreted as UTC by appending 'T00:00:00Z' to 'YYYY-MM-DD' strings.
 *
 * @param {object} personData
 * @param {Date} [today=new Date()] - optional override for deterministic tests
 * @returns {object} filtered personData (non-mutating)
 */

export function filterCasesLastYear(
  personData: dm.PersonData | undefined,
  today: Date = new Date(),
): dm.GeneratedTypes[] {
  const generated = (personData as any)?.generated;
  const parentNTNs: dm.GeneratedTypes[] = Array.isArray(generated?.parentNTNs)
    ? generated.parentNTNs
    : [];

  const toUTCDate = (dStr?: string): Date | null => {
    if (!dStr || typeof dStr !== 'string') return null;
    const normalized = dStr.includes('T') ? dStr : `${dStr}T00:00:00Z`;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const oneYearAgo = new Date(
    Date.UTC(
      today.getUTCFullYear() - 1,
      today.getUTCMonth(),
      today.getUTCDate(),
    ),
  );

  const withinLastYear = (dateStr?: string): boolean => {
    const d = toUTCDate(dateStr);
    return !!d && d >= oneYearAgo;
  };

  // filtering for generated.parentNTNs
  const filteredParents = parentNTNs
    .map((p: dm.GeneratedTypes) => {
      const claims = (p.claims ?? []).filter((c) =>
        withinLastYear(c.createdDate),
      );
      const leaves = (p.leaves ?? []).filter((l) =>
        withinLastYear(l.startDate),
      );
      const accommodations = (p.accommodations ?? []).filter((a) =>
        withinLastYear(a.createdDate),
      );
      return { ...p, claims, leaves, accommodations };
    })
    .filter(
      (p: dm.GeneratedTypes) =>
        (p.claims?.length ?? 0) > 0 ||
        (p.leaves?.length ?? 0) > 0 ||
        (p.accommodations?.length ?? 0) > 0,
    );

  // filtering for "claims in process"
  const cipCandidates: any[] = (() => {
    const pd: any = personData as any;
    if (Array.isArray(pd?.claimsInProcess)) return pd.claimsInProcess;
    if (Array.isArray(pd?.personData)) return pd.personData;
    if (Array.isArray(pd)) return pd;
    return [];
  })();

  const inProcessFiltered = cipCandidates.filter((c) =>
    withinLastYear(c?.startDate),
  );

  const inProcessParents: dm.GeneratedTypes[] = inProcessFiltered.map((c) => ({
    NTNId: 'Claim in process',
    eid: generated?.eid ?? '',
    claims: [],
    leaves: [],
    accommodations: [],
    isIntegrated: false,
    reason: c?.reason ?? '',
    startDate: c?.startDate ?? '',
    endDate: c?.endDate ?? '',
    createdDate: c?.createdDate ?? '',
  }));

  return [...inProcessParents, ...filteredParents];
}

/**
 * set duplicate claims data to session for
 * redirection to duplicate claims page
 */

export interface DuplicateClaimSessionData {
  duplicateClaims: dm.GeneratedTypes[];
  values: Record<string, any>;
  isValid: boolean;
  isLastStep: boolean;
  isPreviousStepsValid: boolean;
}

export function isBrowser(): boolean {
  return (
    globalThis !== undefined &&
    'sessionStorage' in globalThis &&
    globalThis.sessionStorage !== undefined
  );
}

export function makeDupClaimKey(): string {
  try {
    const crypto = (globalThis as any)?.crypto;
    const uuid = crypto?.randomUUID?.();
    if (uuid) return `dup-claim:${uuid}`;

    // Fallback: crypto.getRandomValues is widely supported
    const buf = new Uint8Array(16);
    crypto.getRandomValues(buf);
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join(
      '',
    );
    return `dup-claim:${hex}`;
  } catch {
    // Last resort: timestamp-based key (non-secret, used only for sessionStorage lookup)
    return `dup-claim:${Date.now().toString(36)}`;
  }
}

export function persistDuplicateClaimsSession(
  data: DuplicateClaimSessionData,
): string | null {
  if (!isBrowser()) return null;
  const key = makeDupClaimKey();

  try {
    sessionStorage.setItem(key, JSON.stringify(data));
    return key;
  } catch (err) {
    console.warn('Failed to persist duplicate-claims session data', err);
    return null;
  }
}

/**
 * Handles duplicate claim redirect flow.
 * - Persists session data in sessionStorage under a random key.
 * - Navigates to the duplicate claim route when duplicates exist.
 *
 * @returns true if it handled redirect to duplicate-claim page, false otherwise.
 */

type HistoryLike = {
  push: (args: any) => void;
};

type DuplicateClaimsParam = dm.GeneratedTypes | dm.GeneratedTypes[];

export function redirectIfDuplicateClaims(params: {
  duplicateClaims: DuplicateClaimsParam;
  values: Record<string, any>;
  isValid: boolean;
  isLastStep: boolean;
  isPreviousStepsValid: boolean;
  history: HistoryLike;
  duplicateClaimPathname: string;
}): boolean {
  const {
    duplicateClaims,
    values,
    isValid,
    isLastStep,
    isPreviousStepsValid,
    history,
    duplicateClaimPathname,
  } = params;

  // Normalize to array so downstream logic is consistent

  let parents: dm.GeneratedTypes[] = [];

  if (Array.isArray(duplicateClaims)) {
    parents = duplicateClaims;
  } else if (duplicateClaims) {
    parents = [duplicateClaims];
  }

  if (parents.length > 0) {
    const sessionData: DuplicateClaimSessionData = {
      duplicateClaims: parents,
      values: values,
      isValid: isValid,
      isLastStep: isLastStep,
      isPreviousStepsValid: isPreviousStepsValid,
    };

    const key = persistDuplicateClaimsSession(sessionData);

    history.push({ pathname: duplicateClaimPathname, state: { key } });
    return true;
  }

  return false;
}
